import { useState, useEffect } from "react";
import useSWR from "swr";
import {
    FormProvider,
    useFieldArray,
    useForm,
    useFormContext,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { fetcherSimple, fetcherFull } from "utils/api";
import { getApiErrorMessage, classifyError } from "utils/api-error";
import { toast } from "react-toastify";
import {
    Button,
    IconButton,
    Tooltip,
    Typography,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import { makeEmptyCustomAnswerData } from "utils/consts";
import CustomResultsDialog from "components/dialog/custom-results-dialog";
import RHFTextarea from "./form/rhf-textarea";
import ConfirmReportDialog from "./dialog/confirm-report-dialog";

export default function CustomQuestionResponses(props) {
    const question = props.question;
    const responses = props.responses;
    const customResponses = props.responses.custom;

    const [submittingAnswer, setSubmittingAnswer] = useState(false);

    useEffect(() => {
        if (
            (!customResponses ||
                (customResponses && customResponses.length === 0)) &&
            responses.preset &&
            responses.preset.length === 0
        ) {
            props.setQuestionSaved(false);
        }
    }, [customResponses, responses.preset]);

    // Sibling of preset-question-response.js's schema note: the rules
    // validate a `custom` key the form values never contain (values are
    // keyed `answer`), so effectively nothing client-side validates the
    // answer text sent to POST/PUT. The backend does not validate the
    // text either; reshaping the schema is review-backlog follow-up
    // (F-SEC-A-3).
    const CustomString = Yup.object().shape({
        custom: Yup.array().of(Yup.string().min(1, "Answer is Empty!")),
    });

    const validationSchemaCustom = Yup.object().shape({
        custom: Yup.array().of(CustomString),
    });

    // Every seed site builds fresh row objects. The module-level
    // emptyCustomAnswerData singleton used to be seeded into form state by
    // reference, so resets re-seeded one shared row object over
    // in-progress drafts, and the saved rows were the SWR-cached objects
    // themselves.
    const buildSyncedAnswers = (currentRows = []) => {
        const savedRows = customResponses.map((row) => ({ ...row }));
        // A saved single answer IS the answer: nothing else is carried.
        if (question.answer_type === "single" && savedRows.length > 0) {
            return savedRows;
        }
        // Never-saved rows (empty nanoid) are the respondent's in-progress
        // drafts: carry them over instead of re-seeding an empty row over
        // the typed text.
        const draftRows = currentRows
            .filter((row) => !row.nanoid)
            .map((row) => ({ ...row }));
        const carried =
            draftRows.length > 0 ? draftRows : makeEmptyCustomAnswerData();
        return question.answer_type === "single"
            ? carried
            : [...savedRows, ...carried];
    };

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(validationSchemaCustom),
        defaultValues: { answer: buildSyncedAnswers() },
    });
    const { reset, control, handleSubmit, getValues } = methods;
    const { fields, append, update: updateAnswerRow } = useFieldArray({
        name: "answer",
        control,
    });

    // P5 (enableReinitialize parity), gated on a serialized snapshot of
    // the rows. Through SWR itself, keying on the rows' identity
    // would also work: SWR's dequal compare preserves the cached data
    // reference when a revalidation returns deep-equal data, so unchanged
    // refetches cannot churn the identity. The serialized snapshot
    // additionally guards consumers and test harnesses that hand back
    // FRESH identities for identical data (which bypass SWR's compare).
    // Per-row sorted keys keep the snapshot order-insensitive: the
    // API's JSON encoder has no canonical flag, so Perl's randomized
    // hash order can shuffle row keys between refetches of identical
    // data. Rows are flat objects, so sorting the top level per row is
    // sufficient.
    const savedRowsSnapshot = (customResponses ?? [])
        .map((row) => JSON.stringify(row, Object.keys(row).sort()))
        .join();
    useEffect(() => {
        reset(
            { answer: buildSyncedAnswers(getValues("answer")) },
            { keepErrors: true },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedRowsSnapshot, question.answer_type, reset]);

    const makeEditable = (index) => {
        methods.setValue(`answer.${index}.saved`, false, {
            shouldDirty: true,
        });
        props.update();
        props.setQuestionSaved(false);
    };

    const handleCustomSubmit = () => {
        // left as no-op since child handles submissions
    };

    const deleteCustomAnswer = async (answer) => {
        if (props.previewModeState) return;

        if (
            answer.nanoid !== "" &&
            customResponses.find((c) => c.nanoid === answer.nanoid)
        ) {
            await props.setAnswers({
                preset: responses.preset,
                custom: responses.custom.filter(
                    (c) => c.nanoid !== answer.nanoid,
                ),
            });
            if (answer.is_creator) {
                await fetcherSimple(
                    `/v2/response/question/${question.nanoid}/answer/${answer.nanoid}`,
                    "DELETE",
                );
            }
            props.update();
        }
    };

    const createOrUpdateCustomAnswer = async (index) => {
        if (props.previewModeState) return;
        setSubmittingAnswer(true);

        const answer = getValues("answer");
        if (customResponses[index]?.text === answer[index]?.text) {
            // The saved flag flips without a PUT/POST: the reset re-seeds
            // the row from the responses (the old code flipped it by
            // mutating the SWR-cached row in place) and the
            // refetch re-reports it.
            props.update();
            reset(
                { answer: buildSyncedAnswers(getValues("answer")) },
                { keepErrors: true },
            );
            if (answer[index]?.text) props.setQuestionSaved(true);
            setSubmittingAnswer(false);
            return;
        }

        let result;
        let isEdit = false;
        try {
            if (answer[index].nanoid !== "") {
                isEdit = true;
                result = await fetcherFull(
                    `/v2/response/question/${question.nanoid}/answer/${answer[index].nanoid}`,
                    "PUT",
                    { text: answer[index].text },
                );
            } else {
                result = await fetcherFull(
                    `/v2/response/question/${question.nanoid}/answer`,
                    "POST",
                    { text: answer[index].text },
                );
            }
        } catch (err) {
            // Sibling pattern of respond.js's setAnswers: a failed save
            // toasts instead of propagating silently.
            toast.error(getApiErrorMessage(classifyError(err), "respond"));
            return;
        } finally {
            // The submitting flag must release on failure too, or one
            // failed POST/PUT latches every check button for the rest of
            // the question (no remount resets it).
            setSubmittingAnswer(false);
        }

        if (result.nanoid) {
            props.update(result);
            updateAnswerRow(index, result);
            if (question.answer_type === "multiple" && !isEdit) {
                append(makeEmptyCustomAnswerData()[0]);
            }
            props.setQuestionSaved(true);
        }
    };

    return (
        <>
            {!!(question && customResponses) && (
                <FormProvider {...methods}>
                    <form
                        className="font-body text-black sm:mt-4 mt-2"
                        onSubmit={handleSubmit(handleCustomSubmit)}
                    >
                        {question.answer_type === "single"
                            ? "Your response"
                            : "Your response(s)"}
                        {fields.map((field, index) => (
                            <CustomQuestionResponse
                                // Key by stable identity (I2): RHF
                                // regenerates the field-array ids on every
                                // reset()/update() reseed, so keying by
                                // field.id remounts the row on each save
                                // and wipes its local state (search term,
                                // match popover, dialogs). Saved rows keep
                                // their nanoid across reseeds; drafts fall
                                // back to the generated id.
                                key={field.nanoid || field.id}
                                index={index}
                                question={question}
                                makeEditable={makeEditable}
                                deleteCustomAnswer={deleteCustomAnswer}
                                createOrUpdateCustomAnswer={
                                    createOrUpdateCustomAnswer
                                }
                                submittingAnswer={submittingAnswer}
                                responses={responses}
                                update={props.update}
                                setAnswers={props.setAnswers}
                                previewModeState={props.previewModeState}
                                setQuestionSaved={props.setQuestionSaved}
                                setPresetOther={props.setPresetOther}
                            />
                        ))}
                    </form>
                </FormProvider>
            )}
        </>
    );
}

function CustomQuestionResponse({
    index,
    question,
    makeEditable,
    deleteCustomAnswer,
    createOrUpdateCustomAnswer,
    submittingAnswer,
    responses,
    update,
    setAnswers,
    previewModeState,
    setQuestionSaved,
    setPresetOther,
}) {
    // P4 child: the row reads its own state from the form context; the
    // array helpers stay in the parent.
    const { watch, formState } = useFormContext();
    const { isValid } = formState;
    const singleAnswer = watch(`answer.${index}`);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState({ found: 0 });
    const [openMatchPopover, setOpenMatchPopover] = useState(false);
    const [seenMatchPopover, setSeenMatchPopover] = useState(false);
    const [showCustomResultsDialog, setShowCustomResultsDialog] =
        useState(false);
    const [showConfirmReportDialog, setShowConfirmReportDialog] =
        useState(false);
    const [reportedAnswer, setReportedAnswer] = useState(null);

    const { data: customSearchResult, error: customSearchResultError } = useSWR(
        () =>
            !question.contains_pii &&
            searchTerm.length > 2
                ? [
                    `/v2/response/question/${question.nanoid}/answer?query=${searchTerm}`,
                    "GET",
                ]
                : null,
        ([path, method]) => fetcherSimple(path, method),
    );

    useEffect(() => {
        setSearchResults(customSearchResult);
        if (
            !question.contains_pii &&
            customSearchResult?.found > 0 &&
            seenMatchPopover === false
        ) {
            setOpenMatchPopover(true);
            setSeenMatchPopover(true);
        }
    }, [customSearchResult, seenMatchPopover, question.contains_pii]);

    const onEditInput = async (event) => {
        const value = event.target.value.trim();
        if (
            question.answer_type === "single" &&
            event.target.value.length > 0
        ) {
            setPresetOther(true);
        }
        if (singleAnswer) {
            if(value.length === 0) {
                setQuestionSaved(true);
            } else if (value.length > 0) {
                setQuestionSaved(false);
            }
        }
        setSearchTerm(value);
    };

    const addCustomAnswer = async (values) => {
        if (previewModeState) return;
        if (question.answer_type === "single") {
            if (!values.answer[0] || values.answer[0] === "") return null;
            let newAnswer = searchResults.hits
                .filter((p) => values.answer[0] === p.document.answer_nanoid)
                .map((a) => ({ nanoid: a.document.answer_nanoid }));
            setAnswers({ preset: [], custom: newAnswer });
        } else {
            const newAnswers = searchResults.hits
                .filter((p) =>
                    values.answer.some(
                        (ele) => ele === p.document.answer_nanoid,
                    ),
                )
                .map((a) => ({ nanoid: a.document.answer_nanoid }));
            newAnswers.push(...responses.custom);
            const onlyUnique = (value, index, self) =>
                self.findIndex((ele) => ele.nanoid === value.nanoid) === index;
            newAnswers.filter(onlyUnique);
            setAnswers({ preset: responses.preset, custom: newAnswers });
        }
        setSearchTerm("");
        update();
    };

    return (
        <div>
            {!!customSearchResultError && (
                <div className="font-body">Error getting search results!</div>
            )}

            <div className="flex flex-col font-body pb-2">
                {!!(
                    question.is_view_all &&
                    !question.contains_pii &&
                    !singleAnswer?.saved
                ) && (
                    <div className="flex items-center mb-2 gap-2">
                        <div className="text-body font-bold text-black">
                            Matches:
                        </div>
                        <div>
                            <Tooltip
                                className="border rounded-xl border-blue-gray-500 bg-blue-500 shadow-xl shadow-black/10"
                                open={openMatchPopover}
                                handler={setOpenMatchPopover}
                                placement="right"
                                content={
                                    <div className="flex flex-row">
                                        <div>
                                            <Typography
                                                color="white"
                                                as="span"
                                                align="center"
                                            >
                                                You have matched responses.
                                                <br />
                                                Click on the number to view.
                                            </Typography>
                                        </div>
                                    </div>
                                }
                            >
                                <Button
                                    type="button"
                                    variant="filled"
                                    className="w-10 h-10 !p-0 flex items-center justify-center rounded-lg border-2 border-gray-600 text-sm text-gray-900"
                                    color={
                                        customSearchResult &&
                                        customSearchResult.found > 0
                                            ? "orange"
                                            : "white"
                                    }
                                    disabled={
                                        !customSearchResult ||
                                        customSearchResult.found === 0
                                    }
                                    onClick={() =>
                                        setShowCustomResultsDialog(true)
                                    }
                                >
                                    {customSearchResult
                                        ? customSearchResult.found
                                        : 0}
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                )}
                <div className="flex items-center">
                    <div className="grow md:mr-2">
                        <RHFTextarea
                            autoComplete="off"
                            placeholder="Add a response"
                            variant="static"
                            rows="1"
                            name={`answer.${index}.text`}
                            maxLength={question.answer_limit || 150}
                            className="min-h-0 focus:border-0 font-body pb-1"
                            disabled={singleAnswer?.saved}
                            onKeyUp={onEditInput}
                        />
                    </div>
                    {!!(isValid && !singleAnswer?.saved) && (
                        <IconButton
                            color="green"
                            type="button"
                            variant="filled"
                            className="ml-1"
                            disabled={
                                submittingAnswer ||
                                !isValid ||
                                singleAnswer?.saved ||
                                !(singleAnswer?.text?.trim().length)
                            }
                            onClick={() =>
                                createOrUpdateCustomAnswer(index)
                            }
                        >
                            <MaterialIcon name="check" size="lg" />
                        </IconButton>
                    )}
                </div>
            </div>

            <div className="flex mt-0 border-b border-gray-400 mb-2 pb-1">
                <div className="grow text-platinumheavy md:font-display font-body text-xs md:text-sm">
                    {(singleAnswer?.text ?? "").length} /{" "}
                    {question.answer_limit || 150} characters
                </div>
                <div>
                    {!!singleAnswer?.is_creator && (
                        <IconButton
                            color={singleAnswer?.saved ? "blue" : "gray"}
                            type="button"
                            variant="filled"
                            disabled={!isValid && !singleAnswer?.saved}
                            onClick={() => makeEditable(index)}
                        >
                            <MaterialIcon name="edit" size="lg" />
                        </IconButton>
                    )}
                    {!!singleAnswer?.saved && (
                        <IconButton
                            color="red"
                            type="button"
                            variant="filled"
                            className="ml-1"
                            disabled={!isValid}
                            onClick={() =>
                                deleteCustomAnswer(singleAnswer)
                            }
                        >
                            <MaterialIcon name="close" size="lg" />
                        </IconButton>
                    )}
                </div>
            </div>

            <CustomResultsDialog
                showDialog={showCustomResultsDialog}
                setShowConfirmReportDialog={setShowConfirmReportDialog}
                setReportedAnswer={setReportedAnswer}
                searchResults={searchResults}
                searchTerm={searchTerm}
                question={question}
                responses={responses}
                saveSelection={addCustomAnswer}
                handleClose={() =>
                    setShowCustomResultsDialog(
                        (showCustomResultsDialog) => !showCustomResultsDialog,
                    )
                }
            />
            <ConfirmReportDialog
                showDialog={showConfirmReportDialog}
                showCustomResultsDialog={showCustomResultsDialog}
                question={question}
                reportedAnswer={reportedAnswer}
                setShowConfirmReportDialog={setShowConfirmReportDialog}
                setShowCustomResultsDialog={setShowCustomResultsDialog}
            />
        </div>
    );
}
